import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatCard } from "@/components/ui/stat-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  registrationApprovalApi,
  type PendingRegistration,
  type RegistrationDetails,
} from "@/lib/api/registration-approval";
import { registrationApi } from "@/lib/api/registration";
import {
  presentRegistrationResponses,
  type RegistrationResponseField,
} from "@/lib/registration/present-registration-responses";
import type { Branch } from "@/lib/api/branches";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RejectedRegistrationsPanel } from "@/components/registration/RejectedRegistrationsPanel";
import {
  CheckCircle2,
  XCircle,
  Eye,
  FileText,
  User,
  Mail,
  Phone,
  Calendar,
  Image as ImageIcon,
  Download,
  MapPin,
  ShieldOff,
  UserCheck,
  Loader2,
  ClipboardList,
} from "lucide-react";

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function isLikelyImageDocument(url: string, storageKey: string): boolean {
  let pathname = "";
  try {
    pathname = new URL(url).pathname;
  } catch {
    pathname = (url.split("?")[0] || url).trim();
  }
  const blob = `${pathname} ${storageKey}`.toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(blob);
}

export default function RegistrationApprovalPage() {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [viewingDetails, setViewingDetails] = useState<RegistrationDetails | null>(null);
  const [responseFields, setResponseFields] = useState<RegistrationResponseField[]>([]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [bulkAction, setBulkAction] = useState<"approve" | "reject" | null>(null);
  const [decisionAction, setDecisionAction] = useState<"approve" | "reject" | null>(null);
  const [approvalHomeBranch, setApprovalHomeBranch] = useState("");
  const [queue, setQueue] = useState<"pending" | "rejected">("pending");

  const { isTenantAdmin } = useAccessControl();
  const hasApprovalPermission = user?.permissions?.some(
    (p: string) => p === "registration.approve" || p === "registration.*" || p === "*"
  );
  const canAccess = isTenantAdmin || hasApprovalPermission;

  const { data: branches = [] } = useActiveBranches({ enabled: canAccess });

  useEffect(() => {
    if (!canAccess) return;
    loadRegistrations();
  }, [canAccess]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const data = await registrationApprovalApi.getPendingRegistrations();
      setRegistrations(data);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load pending registrations");
    } finally {
      setLoading(false);
    }
  };

  const filteredRegistrations = useMemo(() => {
    if (!searchQuery) return registrations;
    const q = searchQuery.toLowerCase();
    return registrations.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        r.firstName?.toLowerCase().includes(q) ||
        r.lastName?.toLowerCase().includes(q) ||
        r.phone?.includes(q)
    );
  }, [registrations, searchQuery]);

  const handleViewDetails = async (registrationId: string) => {
    try {
      const details = await registrationApprovalApi.getRegistrationDetails(registrationId);
      const patronTypeId =
        details.patronType?.id ||
        (typeof details.metadata?.patronTypeId === "string"
          ? details.metadata.patronTypeId
          : undefined);
      let fields: RegistrationResponseField[] = [];
      try {
        const config = await registrationApi.getAdminRegistrationConfig(patronTypeId);
        fields = config.formFields;
      } catch {
        fields = [];
      }
      setResponseFields(fields);
      setViewingDetails(details);
      setShowDetailsDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load registration details");
    }
  };

  const handleApproveOrReject = async (registrationId: string, action: "approve" | "reject") => {
    try {
      setDecisionAction(action);
      setUpdating(true);
      setError("");
      setSuccess("");
      await registrationApprovalApi.approveOrReject(registrationId, {
        action,
        notes: actionNotes || undefined,
        ...(action === "approve" && approvalHomeBranch && { homeBranchId: approvalHomeBranch }),
      });
      setSuccess(`Registration ${action === "approve" ? "approved" : "rejected"} successfully`);
      setActionNotes("");
      setApprovalHomeBranch("");
      setShowDetailsDialog(false);
      setViewingDetails(null);
      await loadRegistrations();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${action} registration`);
    } finally {
      setUpdating(false);
      setDecisionAction(null);
    }
  };

  const handleBulkAction = async () => {
    if (selectedIds.size === 0 || !bulkAction) return;
    try {
      setUpdating(true);
      setError("");
      setSuccess("");
      const result = await registrationApprovalApi.bulkApproveOrReject({
        userIds: Array.from(selectedIds),
        action: bulkAction,
        notes: actionNotes || undefined,
      });
      setSuccess(result.message);
      setSelectedIds(new Set());
      setActionNotes("");
      setBulkAction(null);
      await loadRegistrations();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to process bulk action");
    } finally {
      setUpdating(false);
    }
  };

  const regName = (r: PendingRegistration) =>
    [r.firstName, r.lastName].filter(Boolean).join(" ") || "—";

  /* ── Columns ─────────────────────────────────────────── */

  const columns = useMemo<DataTableColumn<PendingRegistration>[]>(
    () => [
      {
        id: "patron",
        header: "Patron",
        cell: (r) => (
          <div>
            <p className="font-medium text-slate-900">{regName(r)}</p>
            <p className="mt-0.5 text-xs text-slate-500">{r.email}</p>
            {r.metadata?.resubmittedAt ? (
              <p className="mt-1 text-xs font-medium text-amber-700">Updated after rejection</p>
            ) : null}
          </div>
        ),
      },
      {
        id: "contact",
        header: "Phone",
        cell: (r) =>
          r.phone ? (
            <span className="flex items-center gap-1 text-slate-600">
              <Phone className="h-3.5 w-3.5 text-slate-400" />
              {r.phone}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
        hideOnMobile: true,
      },
      {
        id: "registered",
        header: "Registered",
        cell: (r) => <span className="text-slate-500">{formatDate(r.createdAt)}</span>,
        hideOnMobile: true,
      },
      {
        id: "actions",
        header: "",
        align: "right",
        cell: (r) => (
          <Button
            variant="outline"
            size="sm"
            className="touch-manipulation"
            onClick={() => handleViewDetails(r.id)}
          >
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            Review
          </Button>
        ),
      },
    ],
    []
  );

  /* ── Mobile card ──────────────────────────────────────── */

  const mobileCard = (r: PendingRegistration, idx: number) => (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium tabular-nums text-slate-400">{idx + 1}.</span>
          <p className="font-heading text-sm font-semibold text-slate-900">{regName(r)}</p>
        </div>
        <p className="mt-0.5 text-xs text-slate-500">{r.email}</p>
        {r.metadata?.resubmittedAt ? (
          <p className="mt-1 text-xs font-medium text-amber-700">Updated after rejection</p>
        ) : null}
        {r.phone && (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
            <Phone className="h-3 w-3" />
            {r.phone}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5">
        <span className="text-xs text-slate-400">{formatDate(r.createdAt)}</span>
        <Button
          variant="outline"
          size="sm"
          className="h-9 touch-manipulation"
          onClick={() => handleViewDetails(r.id)}
        >
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          Review
        </Button>
      </div>
    </div>
  );

  /* ── Access denied ───────────────────────────────────── */

  if (!canAccess) {
    return (
      <EmptyState
        icon={<ShieldOff className="h-6 w-6 text-red-400" />}
        title="Access Denied"
        description="You need permission to approve registrations. Contact your administrator."
      />
    );
  }

  /* ── Render ──────────────────────────────────────────── */

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Registration Approval"
        description="Review pending applications, or reopen and approve rejected ones"
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Registration Approval" },
        ]}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by name, email, or phone…"
      />

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-emerald-200 bg-emerald-50">
          <AlertDescription className="text-emerald-800">{success}</AlertDescription>
        </Alert>
      )}

      <Tabs value={queue} onValueChange={(value) => setQueue(value as "pending" | "rejected")}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {queue === "rejected" ? (
        <RejectedRegistrationsPanel searchQuery={searchQuery} />
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              icon={<ClipboardList className="h-5 w-5" />}
              label="Pending Registrations"
              value={registrations.length}
              iconBg="bg-amber-50 text-amber-600"
            />
          </div>

          {/* Bulk actions bar */}
          {selectedIds.size > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-slate-700">{selectedIds.size} selected</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Notes (optional)…"
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    className="h-9 w-full sm:w-[200px]"
                  />
                  <Button
                    size="sm"
                    className="touch-manipulation"
                    onClick={() => {
                      setBulkAction("approve");
                      handleBulkAction();
                    }}
                    disabled={updating}
                  >
                    {updating && bulkAction === "approve" ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="touch-manipulation"
                    onClick={() => {
                      setBulkAction("reject");
                      handleBulkAction();
                    }}
                    disabled={updating}
                  >
                    {updating && bulkAction === "reject" ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircle className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <DataTable
            columns={columns}
            data={filteredRegistrations}
            rowKey={(r) => r.id}
            loading={loading}
            selectable
            selectedIds={selectedIds}
            onSelectChange={setSelectedIds}
            showSerialNumber
            mobileCard={mobileCard}
            emptyState={
              <EmptyState
                icon={<UserCheck className="h-6 w-6 text-emerald-500" />}
                title="No pending registrations"
                description="All registrations have been reviewed. New ones will appear here."
              />
            }
          />
        </>
      )}

      {/* ── Details Dialog ────────────────────────────────── */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent
          className="flex max-h-[min(90vh,860px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
          onClose={() => setShowDetailsDialog(false)}
        >
          <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="font-heading text-lg font-semibold text-slate-900">
                  Registration Details
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-slate-500">
                  Review all information before approving or rejecting.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {viewingDetails && (
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {viewingDetails.metadata?.resubmittedAt ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <p className="font-semibold">This applicant corrected a rejected application</p>
                  <p className="mt-1">
                    Submitted again {formatDate(String(viewingDetails.metadata.resubmittedAt))}.
                    {viewingDetails.metadata.rejectionNotes
                      ? ` Previous reason: ${String(viewingDetails.metadata.rejectionNotes)}`
                      : ""}
                  </p>
                </div>
              ) : null}
              {/* Personal & enrollment */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-slate-400">First Name</Label>
                  <p className="font-medium text-slate-900">{viewingDetails.firstName || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Last Name</Label>
                  <p className="font-medium text-slate-900">{viewingDetails.lastName || "N/A"}</p>
                </div>
                <div>
                  <Label className="flex items-center gap-1 text-xs text-slate-400">
                    <Mail className="h-3 w-3" /> Email
                  </Label>
                  <p className="font-medium text-slate-900">{viewingDetails.email}</p>
                </div>
                {viewingDetails.phone ? (
                  <div>
                    <Label className="flex items-center gap-1 text-xs text-slate-400">
                      <Phone className="h-3 w-3" /> Phone
                    </Label>
                    <p className="font-medium text-slate-900">{viewingDetails.phone}</p>
                  </div>
                ) : null}
                {viewingDetails.patronType ? (
                  <div>
                    <Label className="text-xs text-slate-400">Patron type</Label>
                    <p className="font-medium text-slate-900">{viewingDetails.patronType.name}</p>
                  </div>
                ) : viewingDetails.metadata?.patronTypeId ? (
                  <div>
                    <Label className="text-xs text-slate-400">Patron type</Label>
                    <p className="font-mono text-xs text-slate-600">
                      {viewingDetails.metadata.patronTypeId}
                    </p>
                  </div>
                ) : null}
                {viewingDetails.homeBranch ? (
                  <div>
                    <Label className="text-xs text-slate-400">Preferred home branch</Label>
                    <p className="font-medium text-slate-900">
                      {viewingDetails.homeBranch.name}
                      <span className="text-slate-500"> ({viewingDetails.homeBranch.code})</span>
                    </p>
                  </div>
                ) : null}
                <div>
                  <Label className="flex items-center gap-1 text-xs text-slate-400">
                    <Calendar className="h-3 w-3" /> Account created
                  </Label>
                  <p className="font-medium text-slate-900">
                    {formatDate(viewingDetails.createdAt)}
                  </p>
                </div>
                {viewingDetails.metadata?.registrationDate ? (
                  <div>
                    <Label className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar className="h-3 w-3" /> Registration submitted
                    </Label>
                    <p className="font-medium text-slate-900">
                      {formatDate(viewingDetails.metadata.registrationDate)}
                    </p>
                  </div>
                ) : null}
              </div>

              {(viewingDetails.metadata?.paymentRequired ||
                viewingDetails.metadata?.registrationFee != null) && (
                <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm">
                  <p className="font-semibold text-slate-800">Payment</p>
                  <p className="mt-1 text-slate-600">
                    Fee (metadata):{" "}
                    <span className="font-medium tabular-nums text-slate-900">
                      {String(viewingDetails.metadata.registrationFee ?? "—")}
                    </span>
                    {viewingDetails.metadata.paymentCompleted ? (
                      <span className="ml-2 text-emerald-700">
                        Recorded as paid at registration
                      </span>
                    ) : viewingDetails.metadata.paymentRequired ? (
                      <span className="ml-2 text-amber-800">
                        Payment may be required before approval
                      </span>
                    ) : null}
                  </p>
                </div>
              )}

              {(viewingDetails.profilePicture || viewingDetails.metadata?.profilePictureKey) && (
                <div>
                  <Label className="flex items-center gap-1 text-xs text-slate-400">
                    <ImageIcon className="h-3 w-3" /> Profile picture
                  </Label>
                  {viewingDetails.profilePicture ? (
                    <a
                      href={viewingDetails.profilePicture}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block max-w-full"
                    >
                      <img
                        src={viewingDetails.profilePicture}
                        alt="Profile"
                        className="max-h-64 max-w-[280px] rounded-lg border border-slate-200 object-contain"
                      />
                    </a>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">
                      File on record (storage key:{" "}
                      <code className="rounded bg-slate-100 px-1 text-xs">
                        {String(viewingDetails.metadata.profilePictureKey)}
                      </code>
                      ) — open failed; ask ops to check storage.
                    </p>
                  )}
                </div>
              )}

              {Array.isArray(viewingDetails.metadata?.requiredDocuments) &&
                viewingDetails.metadata.requiredDocuments.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <ClipboardList className="h-4 w-4" />
                      Required document types
                    </h4>
                    <ul className="list-inside list-disc space-y-0.5 text-sm text-slate-600">
                      {viewingDetails.metadata.requiredDocuments.map((label: string) => (
                        <li key={label}>{label}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {viewingDetails.metadata?.formData &&
              typeof viewingDetails.metadata.formData === "object" &&
              Object.keys(viewingDetails.metadata.formData).length > 0 ? (
                <div>
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FileText className="h-4 w-4" />
                    Registration form responses
                  </h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {presentRegistrationResponses(
                      viewingDetails.metadata.formData as Record<string, unknown>,
                      responseFields
                    ).map((row) => (
                      <div key={row.key} className={row.multiline ? "sm:col-span-2" : ""}>
                        <Label className="text-xs text-slate-400">{row.label}</Label>
                        {row.multiline ? (
                          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-2 font-mono text-xs text-slate-800">
                            {row.text}
                          </pre>
                        ) : (
                          <p className="break-words font-medium text-slate-900">{row.text}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {viewingDetails.documents && viewingDetails.documents.length > 0 ? (
                <div>
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FileText className="h-4 w-4" />
                    Uploaded files ({viewingDetails.documents.length})
                  </h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {viewingDetails.documents.map((doc, i) => (
                      <div
                        key={`${doc.key}-${i}`}
                        className="rounded-lg border border-slate-200 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-sm font-medium text-slate-800">
                              {doc.name || doc.type}
                            </span>
                            <p className="mt-0.5 break-all font-mono text-[10px] text-slate-400">
                              {doc.key}
                            </p>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 rounded-md p-1.5 text-primary hover:bg-primary/10"
                            title="Download / open"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </div>
                        {doc.url ? (
                          <div className="mt-2">
                            {isLikelyImageDocument(doc.url, doc.key) ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block overflow-hidden rounded border border-slate-100"
                              >
                                <img
                                  src={doc.url}
                                  alt={doc.name}
                                  className="h-36 w-full object-cover"
                                />
                              </a>
                            ) : (
                              <div className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded border border-slate-100 bg-slate-50 px-2">
                                <FileText className="h-8 w-8 text-slate-300" />
                                <span className="text-center text-xs text-slate-500">
                                  Preview not available — use download
                                </span>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Home branch */}
              {branches.length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-primary/10 bg-primary/5 p-4">
                  <Label className="flex items-center gap-1.5 text-sm font-medium text-primary">
                    <MapPin className="h-4 w-4" />
                    Assign Home Branch
                  </Label>
                  <Select value={approvalHomeBranch} onValueChange={setApprovalHomeBranch}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select a branch (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b: Branch) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                          {b.isMain ? " — Main Library" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-primary/70">
                    Leave blank to keep the patron's preference.
                  </p>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Note for the applicant</Label>
                <Textarea
                  placeholder="If you reject, this is emailed as the reason. Say what they need to correct."
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
          )}

          {/* Footer actions */}
          {viewingDetails && (
            <DialogFooter className="flex-col gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row">
              <Button
                className="min-h-11 flex-1 touch-manipulation sm:min-h-10"
                onClick={() => handleApproveOrReject(viewingDetails.id, "approve")}
                disabled={updating}
              >
                {decisionAction === "approve" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                Approve
              </Button>
              <Button
                variant="destructive"
                className="min-h-11 flex-1 touch-manipulation sm:min-h-10"
                onClick={() => handleApproveOrReject(viewingDetails.id, "reject")}
                disabled={updating}
              >
                {decisionAction === "reject" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Reject
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
